import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Col, Row, Typography, Tabs } from 'antd';
import axios from 'axios';
import { LogoutOutlined, LoadingOutlined } from '@ant-design/icons'
import logo from '../logo_no_bg_free.png';
import { useDialog } from './DialogProvider';
import { readAiDraftCacheFromLocalStorage, syncAiDraftCacheFromUserInfo } from "../utils/aiDraftCache";

const { Title } = Typography;
const { TabPane } = Tabs;

const isLocal = window.location.origin.includes("localhost");
const backendBaseUrl = isLocal ? "http://localhost:8000" : "";

// Apple-inspired glassmorphism styles to ensure legibility over background
const pageWrapStyle = {
    height: "100%",
    minHeight: "100%",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    position: "relative",
    zIndex: 1
};

const glassCardStyle = {
    width: "100%",
    maxWidth: 640,
    borderRadius: 16,
    background: "rgba(255, 255, 255, 0.18)",
    backdropFilter: "saturate(180%) blur(16px)",
    WebkitBackdropFilter: "saturate(180%) blur(16px)",
    border: "1px solid rgba(255, 255, 255, 0.35)",
    boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
    overflow: "hidden"
};

const glassContentStyle = {
    padding: 24
};

const logoStyle = { height: 50, display: "block", margin: "8px auto 8px" };
const titlePrimaryStyle = { textAlign: "center", color: "#031932", marginTop: 8, marginBottom: 0 };
const titleSecondaryStyle = { textAlign: "center", color: "grey", marginTop: 4, marginBottom: 12, fontSize: 14 };
const landingLinkStyle = { textAlign: "center", marginTop: 6, marginBottom: 10 };

const formColBase = (isMobile) => ({
    width: "100%",
    maxWidth: isMobile ? 320 : 520,
    padding: 0,
    border: 0,
    background: "transparent",
    boxShadow: "none",
    margin: "0 auto"
});

function LoginForm({ username, setUsername, title, slogan, setIsAdmin, setIsPremium, setIsBetaTester, setFTP, setWeight, setFavouritePlans, isMobile, setHistory, reloadTs, setOtherUserInfo, setNextSessionUuid, rideOutsideProposed, setRideOutsideProposed, appBootstrappingUserInfo = false, refreshUserInfoAndHistory }) {
    const { alert: alertDlg, confirm } = useDialog();
    const [key, setKey] = useState("1")
    const [isSigningIn, setIsSigningIn] = useState(false);
    const [isFetchingUserInfo, setIsFetchingUserInfo] = useState(false);
    const [isSigningUp, setIsSigningUp] = useState(false);
    const isBusy = isSigningIn || isFetchingUserInfo || !!appBootstrappingUserInfo;

    const axiosInstance = axios.create({
        baseURL: "/api", // Adjust this baseURL to where your backend is hosted
        headers: {
            'Content-Type': 'application/json'
        }
    });

    const axiosInstanceWithToken = axios.create({
        baseURL: "/api", // Adjust this baseURL to where your backend is hosted
        headers: {
            'Content-Type': 'application/json'
        }
    });


    useEffect(() => {
        if (username) {
            getUserInfo()
        }
    }, [reloadTs, username]);


    useEffect(() => {
        const timeout = setTimeout(() => {
            if (!username) {
                // Fast local cache restore (useful right after a refresh while network loads)
                try {
                    const { prog, single, progOngoing, singleOngoing } = readAiDraftCacheFromLocalStorage();
                    if (prog || single) {
                        setOtherUserInfo((prev) => ({
                            ...(prev || {}),
                            lastAiPlanBuilder: prog || null,
                            hasOngoingAiPlanBuilder: Boolean(progOngoing),
                            lastAiPlanBuilderSingle: single || null,
                            hasOngoingAiPlanBuilderSingle: Boolean(singleOngoing),
                        }));
                    }
                } catch (e) {
                    // ignore
                }
                getUserInfo();
            }
        }, 100); // Wait for 300ms before running, to be sure token is well removed when logged out

        return () => clearTimeout(timeout); // Clean up on re-render
    }, []);


    // Attach token to every request if available  Logic needs to be updated in SaveTraining.js as well + Login + App.js
    axiosInstanceWithToken.interceptors.request.use(config => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    }, error => {
        return Promise.reject(error);
    });

    // Handle response errors Logic needs to be updated in SaveTraining.js as well + Login + App.js
    axiosInstanceWithToken.interceptors.response.use(
        response => response,
        async error => {
            const originalRequest = error.config;

            // Check if the error is due to an expired access token
            if (error.response && error.response.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true; // Prevent infinite retries

                try {
                    // Call /refresh/token to get new tokens
                    const refreshToken = localStorage.getItem('refresh_token');
                    const refreshResponse = await axios.post('api/refresh_tokens/', { refresh_token: refreshToken });

                    if (refreshResponse.status === 200) {
                        const { access_token: newToken, refresh_token: newRefreshToken } = refreshResponse.data;

                        // Store new tokens in localStorage
                        localStorage.setItem('token', newToken);
                        localStorage.setItem('refresh_token', newRefreshToken);

                        // Update Authorization header and retry the original request
                        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                        return axiosInstanceWithToken(originalRequest);
                    }
                } catch (refreshError) {
                    alertDlg("You’ve been inactive for too long. You will now be logged out.")
                    console.error('Failed to refresh token:', refreshError);
                    localStorage.removeItem('token'); // Remove the stored token
                    localStorage.removeItem('refresh_token'); // Remove the stored token
                    setUsername(null)
                    console.log('Logged out');
                    // Optionally, handle token refresh failure (e.g., logout user)
                }
            }

            return Promise.reject(error);
        }
    );

    const getUserInfo = async (fetchShouldRideOutside = false, addLogin = false) => {
        if (!localStorage.getItem('token')) return;

        // App.js is the canonical bootstrapper. Avoid duplicate calls when App is already fetching / has fetched.
        try {
            if (window.__srUserInfoFetchInFlight || window.__srUserInfoBootstrapped) {
                console.log("[get_user_info] Login.getUserInfo skipped (App bootstrap already in progress/done)");
                return;
            }
        } catch { }

        setIsFetchingUserInfo(true);
        try {
            if (typeof refreshUserInfoAndHistory === "function") {
                await refreshUserInfoAndHistory({
                    includeHistory: false,
                    // On login, validate Strava once during the initial user-info fetch.
                    validateStrava: Boolean(addLogin) ? true : !fetchShouldRideOutside,
                    fetchShouldRideOutside: !!fetchShouldRideOutside,
                    addLogin: Boolean(addLogin),
                });
            } else {
                // Legacy fallback (should be rare): do the request locally.
                let clientIp = null;
                if (fetchShouldRideOutside) {
                    const ipResponse = await fetch("https://api64.ipify.org?format=json");
                    const ipData = await ipResponse.json();
                    clientIp = ipData.ip;
                }
                const shouldValidateStrava = Boolean(addLogin) ? true : !fetchShouldRideOutside;
                const __srGetUserInfoId =
                    (window.__srGetUserInfoNextId = (window.__srGetUserInfoNextId || 0) + 1);
                const __srGetUserInfoSource = `Login.getUserInfo(fetchShouldRideOutside=${fetchShouldRideOutside})`;
                await axiosInstanceWithToken.post('/get_user_info/', {
                    "fetchShouldRideOutside": fetchShouldRideOutside ? clientIp : null,
                    "validateStrava": shouldValidateStrava,
                    "exclude_history": true,
                    "add_login": Boolean(addLogin),
                    "debug_source": `${__srGetUserInfoSource}#${__srGetUserInfoId}`,
                }).then((response) => {
                    if (response.data.refresh_token) {
                        localStorage.setItem("token", response.data.access_token);
                        localStorage.setItem("refresh_token", response.data.refresh_token);
                    }
                    // Minimal state hydration (fallback only; canonical is App.js)
                    setUsername(response.data.username);
                    setIsAdmin(response.data.is_admin);
                    setIsPremium && setIsPremium(Boolean(response.data.is_premium));
                    setIsBetaTester && setIsBetaTester(Boolean(response.data.is_beta_tester));
                    setFTP(response.data.ftp || 250);
                    setWeight(response.data.weight || 75);
                    setFavouritePlans(response.data.favourite || []);
                    setNextSessionUuid && setNextSessionUuid(response.data.next_session_uuid || null);
                    try { syncAiDraftCacheFromUserInfo(response.data); } catch { }
                    setOtherUserInfo(prevInfo => ({
                        ...prevInfo,
                        stravaRefreshToken: response.data.strava_refresh_token || prevInfo?.stravaRefreshToken || null,
                        loginCount: (() => {
                            const raw =
                                response.data?.login_count ??
                                response.data?.loginCount ??
                                response.data?.other_info?.login_count ??
                                response.data?.other_info?.loginCount ??
                                null;
                            if (raw === null || raw === undefined) return prevInfo?.loginCount ?? null;
                            const n = Number(raw);
                            return Number.isFinite(n) ? n : (prevInfo?.loginCount ?? null);
                        })(),
                        shouldRideOutside: response.data.other_info?.should_ride_outside ?? prevInfo?.shouldRideOutside,
                        last_login: response.data.other_info?.last_login_ts ?? prevInfo?.last_login,
                        email: response.data.email || response.data.other_info?.email || prevInfo?.email || null,
                        bgUrl: response.data.other_info?.bg_url || prevInfo?.bgUrl || null,
                        bgUrlPath: response.data.other_info?.bg_url_path || prevInfo?.bgUrlPath || null,
                        lastAiPlanBuilder: response.data.last_ai_plan_builder || null,
                        hasOngoingAiPlanBuilder: Boolean(response.data.has_ongoing_ai_plan_builder),
                        lastAiPlanBuilderSingle: response.data.last_ai_plan_builder_single || null,
                        hasOngoingAiPlanBuilderSingle: Boolean(response.data.has_ongoing_ai_plan_builder_single),
                    }));
                });
            }
        } finally {
            setIsFetchingUserInfo(false);
        }
    };

    const handleSubmit = async (values) => {
        try {
            setIsSigningIn(true);
            const response = await axios.post(`/api/token/`, new URLSearchParams({
                username: values.username,
                password: values.password
            }));
            console.log("token received")

            localStorage.setItem('token', response.data.access_token); // Store the token
            localStorage.setItem('refresh_token', response.data.refresh_token); // Store the token
            // Hydrate all user state from the single canonical endpoint (avoids duplication with App.js logic).
            try {
                // If the user logged out and in again without a full reload, App may have left a bootstrap guard behind.
                // Clear it so the post-login fetch is never skipped.
                delete window.__srUserInfoBootstrapped;
                delete window.__srUserInfoFetchInFlight;
            } catch { }
            // Fetch canonical user info immediately after token (and compute ride-outside if needed).
            await getUserInfo(true, true);
        } catch (error) {
            alertDlg(
                <span>
                    We couldn’t sign you in. The username or email and password didn’t match. Make sure you have verified your email. If you forgot your password,{" "}
                    <a href={`${backendBaseUrl}/api/reset_password/reset/`} target="_blank" rel="noreferrer noopener">
                        reset it here
                    </a>
                    .
                </span>
            )
            //alert('Failed to login, please verify your username and password. If you just signed up, be sure you have clicked on the verification link that has been sent to you via email. If you have never received such email or you forgot your password, please contact support at sam@simplyride.app');
            console.error('Failed to login, please verify your username and password.', error);
        }
        finally {
            setIsSigningIn(false);
        }
    };
    const handleSubmitSignUp = async (values) => {
        if (values.password !== values.password2 || values.password.length < 8) {
            alertDlg("Both passwords needs to be identical with at least 8 characters");
            return;
        }
        const data = {
            username: values.username,
            password: values.password,
            full_name: values.fullName,
            email: values.email
            //access_key: values.accessKey
        }
        try {
            setIsSigningUp(true);
            const response = await axiosInstance.post('/sign_up_with_access_key/', { data });
            alertDlg(`Account created successfully, ${response.data.username}! Please check your email (including spam folder) for a verification link. Click the link to verify your address and start riding!`);
            setKey("1")
        } catch (error) {
            console.log("e", error);
            alertDlg(
                <span>
                    We couldn’t create your account. The username might already be taken. It can also happen if the email is already in use.{" "}
                    If that email is yours,{" "}
                    <a href={`${backendBaseUrl}/api/reset_password/reset/`} target="_blank" rel="noreferrer noopener">
                        reset your password here
                    </a>{" "}
                    and then sign in.
                </span>
            );
        } finally {
            setIsSigningUp(false);
        }
    }



    const handleLogout = () => {
        localStorage.removeItem('token'); // Remove the stored token
        localStorage.removeItem('refresh_token'); // Remove the stored token
        try {
            delete window.__srUserInfoBootstrapped;
            delete window.__srUserInfoFetchInFlight;
        } catch { }
        setUsername(null)
        console.log('Logged out');
    };
    return (
        <>{!username && (
        <div style={pageWrapStyle}>
          <div style={glassCardStyle}>
            <div style={glassContentStyle}>
              <img src={logo} style={logoStyle} alt="SimplyRide.app" />
              <Title style={titlePrimaryStyle} level={2}>{title}</Title>
              <Title style={titleSecondaryStyle} level={5}><small>{slogan}</small></Title>
              <div style={landingLinkStyle}>
                <Button
                  type="link"
                  onClick={() => {
                    window.open("https://simplyride.app/landing", "_blank", "noopener,noreferrer");
                  }}
                >
                  What is SimplyRide.app?
                </Button>
              </div>
              <Tabs
                defaultActiveKey={key}
                activeKey={key}
                onChange={(k) => setKey(k)}
                style={{ marginTop: 8 }}
                centered
                tabBarStyle={{ marginBottom: 16 }}
              >
                <TabPane tab="Sign in" key="1">
                    <Form onFinish={handleSubmit} layout="vertical">
                        <Row>
                            <Col style={formColBase(isMobile)}>
                                <Form.Item
                                    block
                                    label="Username or email"
                                    name="username"
                                    rules={[{ required: true, message: 'Please input your username or email!' }]}
                                >
                                    <Input block disabled={isBusy} />
                                </Form.Item>
                                <Form.Item
                                    label="Password"
                                    name="password"
                                    rules={[{ required: true, message: 'Please input your password!' }]}
                                >
                                    <Input.Password disabled={isBusy} />
                                </Form.Item>
                                <Form.Item>
                                    <Button type="primary" className="primary-glass-btn" htmlType="submit" disabled={isBusy}>
                                        {isBusy ? (
                                          <>
                                            <LoadingOutlined spin style={{ marginRight: 8 }}/> Logging in…
                                          </>
                                        ) : (
                                          "Log In"
                                        )}
                                    </Button>
                                </Form.Item>
                                <a href={`${backendBaseUrl}/api/reset_password/reset/`} target="_blank" rel="noreferrer noopener">Reset password</a>
                            </Col>
                        </Row>
                    </Form>
                </TabPane>
                <TabPane tab="Sign up" key="2">
                    <Form onFinish={handleSubmitSignUp} layout="vertical" style={{ marginTop: 12 }}>
                        <Row>
                            <Col style={formColBase(isMobile)}>
                                <Form.Item
                                    block
                                    label="Username"
                                    name="username"
                                    rules={[
                                        { required: true, message: 'Please input your username!' },
                                        { pattern: /^[a-zA-Z0-9]+$/, message: "Username can only contain letters and numbers." }
                                    ]}
                                >
                                    <Input block placeholder="Letters and numbers only" />
                                </Form.Item>
                                <Form.Item
                                    block
                                    label="Full Name"
                                    name="fullName"
                                    rules={[{ required: true, message: 'Please input your username!' }]}
                                >
                                    <Input block />
                                </Form.Item>
                                <Form.Item
                                    block
                                    label="Email"
                                    name="email"
                                    rules={[{ required: true, message: 'Please input your username!' }]}
                                >
                                    <Input block />
                                </Form.Item>
                                <Form.Item
                                    label="Password"
                                    name="password"
                                    help="Min 8 characters."
                                    rules={[{ required: true, message: 'Please input your password!' }]}
                                >
                                    <Input.Password />
                                </Form.Item>
                                <Form.Item
                                    label="Repeat password"
                                    name="password2"
                                    rules={[{ required: true, message: 'Please input your password!' }]}
                                >
                                    <Input.Password />
                                </Form.Item>
                                <Form.Item
                                    hidden
                                    label="Access key"
                                    name="accessKey"
                                    help="As this is a beta version, only invited users can register for now. Feel free to contact me at sam@simplyride.app to get one ;)"
                                    rules={[{ required: false, message: 'Please input your password!' }]}
                                >
                                    <Input.Password />
                                </Form.Item>
                                <Form.Item style={{ marginTop: 12 }}>
                                    <Button
                                      type="primary"
                                      className="primary-glass-btn"
                                      htmlType="submit"
                                      disabled={isSigningUp}
                                    >
                                        {isSigningUp ? (
                                          <>
                                            <LoadingOutlined spin /> Signing up…
                                          </>
                                        ) : (
                                          "Sign up"
                                        )}
                                    </Button>
                                </Form.Item>
                                <div>
                                    <a href={`${backendBaseUrl}/api/reset_password/reset/`} target="_blank" rel="noreferrer noopener">Reset password</a>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </TabPane>
              </Tabs>
              {isMobile && (
                <i style={{ display: "block", marginTop: 12 }}>
                  <Col style={{ maxWidth: 320, margin: "0 auto", textAlign: "justify" }}>
                    Note: You are accessing <b>the mobile version, best used in landscape mode.</b><br />
                    For <b>full features</b> (custom plans, more training data), use a <b>computer</b>.<br />
                  </Col>
                </i>
              )}
            </div>
          </div>
        </div>)
        }

            {
                username && (
                    <Button block onClick={async () => { if (await confirm("Leaving already? Any ongoing session or training will be lost. See you next time!")) handleLogout(); }}>
                        <LogoutOutlined /> {username}
                    </Button>
                )
            }
        </>
    );
}

export default LoginForm;
